import { useState, useEffect } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/Layout";
import { toast } from "sonner";
import { Upload, ArrowRight, Download, Check, History } from "lucide-react";

// Contact Name is composed from First + Last (Apollo/most CRMs export split names).
const FIELDS = [
  { key: "account_name", label: "Account Name (Company) *", req: true, aliases: ["company name", "company", "account name", "account"] },
  { key: "first_name", label: "First Name *", req: true, aliases: ["first name", "firstname", "given name"] },
  { key: "last_name", label: "Last Name *", req: true, aliases: ["last name", "lastname", "surname", "family name"] },
  { key: "email", label: "Email (dedupe key)", aliases: ["email", "email address", "work email"] },
  { key: "title", label: "Title", aliases: ["title", "job title"] },
  { key: "phone", label: "Phone", aliases: ["mobile phone", "work direct phone", "direct phone", "corporate phone", "phone", "phone number"] },
  { key: "industry", label: "Industry", aliases: ["industry"] },
  { key: "size", label: "Company Size", aliases: ["# employees", "employees", "company size", "size"] },
  { key: "geography", label: "Geography", aliases: ["country", "geography", "location", "company country"] },
  { key: "linkedin_url", label: "LinkedIn URL", aliases: ["person linkedin url", "linkedin url", "linkedin", "person linkedin"] },
];

// Best-Phone fallback priority when the mapped phone cell is empty.
const PHONE_COLS = ["mobile phone", "work direct phone", "direct phone", "corporate phone", "other phone", "phone"];

// RFC-style CSV line parser: handles quoted fields with embedded commas + "" escapes.
function splitCSVLine(line) {
  const out = [];
  let cur = "", inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQ) {
      if (ch === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++; } else inQ = false;
      } else cur += ch;
    } else if (ch === '"') inQ = true;
    else if (ch === ",") { out.push(cur); cur = ""; }
    else cur += ch;
  }
  out.push(cur);
  return out.map((c) => c.trim());
}

// Vendor ownership columns must never be importable — stripped at the parse layer so they
// can't be selected as a source for ANY target field (mirrors backend _BANNED_OWNER_KEYS).
const BANNED_OWNER_KEYS = new Set([
  "owner", "ownername", "ownerid", "owneremail",
  "contactowner", "contactownerid", "contactowneremail", "contactownername",
  "accountowner", "accountownerid", "accountowneremail", "accountownername",
  "assignedowner", "recordowner", "leadowner", "opportunityowner",
]);
const isOwnerCol = (h) => BANNED_OWNER_KEYS.has(String(h).replace(/[\s_\-.]+/g, "").toLowerCase());

function parseCSV(text) {
  // join physical lines that fall inside a quoted field
  const raw = text.replace(/\r\n/g, "\n").trim();
  const records = [];
  let cur = "", q = 0;
  for (const ch of raw) {
    if (ch === '"') q++;
    if (ch === "\n" && q % 2 === 0) { records.push(cur); cur = ""; }
    else cur += ch;
  }
  if (cur) records.push(cur);
  const clean = records.filter((l) => l.trim());
  if (!clean.length) return { headers: [], rows: [] };
  const rawHeaders = splitCSVLine(clean[0]);
  // Drop owner columns entirely (header + aligned cell in every row) before mapping is possible.
  const keep = rawHeaders.map((h, i) => (isOwnerCol(h) ? -1 : i)).filter((i) => i >= 0);
  const headers = keep.map((i) => rawHeaders[i]);
  const rows = clean.slice(1).map((l) => { const c = splitCSVLine(l); return keep.map((i) => c[i]); });
  return { headers, rows };
}

function autoMap(headers) {
  const norm = headers.map((h) => h.toLowerCase().trim());
  const map = {};
  FIELDS.forEach((f) => {
    let hit = -1;
    for (const a of f.aliases) { hit = norm.indexOf(a); if (hit >= 0) break; }
    if (hit < 0) for (const a of f.aliases) { hit = norm.findIndex((h) => h.includes(a)); if (hit >= 0) break; }
    if (hit >= 0) map[f.key] = hit;
  });
  return map;
}

export default function BulkImport() {
  const [parsed, setParsed] = useState(null);
  const [mapping, setMapping] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filename, setFilename] = useState("");
  const [assigned, setAssigned] = useState({});
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [progress, setProgress] = useState(null);
  const [reps, setReps] = useState([]);
  const [assignTo, setAssignTo] = useState("");

  const loadHistory = () => api.get("/import-history").then(({ data }) => setHistory(data)).catch(() => {});
  useEffect(() => {
    loadHistory();
    if (user.role === "admin" || user.role === "supervisor") {
      api.get("/users").then(({ data }) => {
        const list = Array.isArray(data) ? data : (data.items || data.users || []);
        setReps(list.filter((u) => ["rep", "supervisor"].includes(u.role) && u.status !== "departed"));
      }).catch(() => {});
    }
  }, [user.role]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFilename(f.name);
    const reader = new FileReader();
    reader.onload = () => {
      const p = parseCSV(String(reader.result));
      setParsed(p);
      setMapping(autoMap(p.headers));
      setResult(null);
      setAssigned({});
      setProgress(null);
    };
    reader.readAsText(f);
  };

  const mappingValid = mapping.account_name != null && mapping.first_name != null && mapping.last_name != null;

  const bestPhoneFrom = (cells) => {
    for (const name of PHONE_COLS) {
      const i = parsed.headers.findIndex((h) => h.toLowerCase().trim() === name);
      if (i >= 0 && (cells[i] || "").trim()) return cells[i].trim();
    }
    return "";
  };

  const buildRows = () => parsed.rows.map((cells) => {
    const obj = {};
    FIELDS.forEach((f) => {
      if (f.key === "first_name" || f.key === "last_name") return;
      if (mapping[f.key] != null) obj[f.key] = cells[mapping[f.key]];
    });
    const first = (cells[mapping.first_name] || "").trim();
    const last = (cells[mapping.last_name] || "").trim();
    obj.contact_name = `${first} ${last}`.trim();
    if (!(obj.phone || "").trim()) obj.phone = bestPhoneFrom(cells);   // Best-Phone: Mobile→Work→Corporate
    return obj;
  });

  const previewRows = mappingValid && parsed ? buildRows().slice(0, 5) : [];

  const downloadDedupeReport = () => {
    const rows = result?.skipped_details || [];
    const header = "row,contact_name,email,reason";
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const body = rows.map((r) => [r.row, r.contact_name, r.email, r.reason].map(esc).join(",")).join("\n");
    const blob = new Blob([header + "\n" + body], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "dedupe-report.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const assignRep = async (l) => {
    try {
      await api.post(`/leads/${l.lead_id}/assign`, { assigned_to: l.suggested_rep.id });
      setAssigned((a) => ({ ...a, [l.lead_id]: l.suggested_rep.name }));
      toast.success(`Assigned to ${l.suggested_rep.name}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Assign failed"); }
  };

  const assignAll = async () => {
    const pending = (result?.created_leads || []).filter((l) => l.suggested_rep && !assigned[l.lead_id]);
    if (!pending.length) return;
    try {
      const { data } = await api.post("/leads/bulk-assign", {
        assignments: pending.map((l) => ({ lead_id: l.lead_id, assigned_to: l.suggested_rep.id })),
      });
      const map = {};
      pending.forEach((l) => { if (!data.failed?.some((f) => f.lead_id === l.lead_id)) map[l.lead_id] = l.suggested_rep.name; });
      setAssigned((a) => ({ ...a, ...map }));
      toast.success(`${data.assigned} assigned${data.failed?.length ? ` · ${data.failed.length} skipped` : ""}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk assign failed"); }
  };

  const undoImport = async (h) => {
    if (!window.confirm(`Roll back "${h.filename}"? This deletes the ${h.created} lead(s) it created.`)) return;
    try {
      const { data } = await api.post(`/import-history/${h.id}/undo`);
      toast.success(`Rolled back · ${data.leads_removed} leads removed`);
      setResult(null);
      loadHistory();
    } catch (e) { toast.error(e.response?.data?.detail || "Undo failed"); }
  };

  const doImport = async () => {
    if (!mappingValid) {
      toast.error("Map Account Name, First Name and Last Name first");
      return;
    }
    const CHUNK = 200; // internal upload batch size — NOT a cap; the full file is sent as sequential batches
    const rows = buildRows();
    const total = rows.length;
    setBusy(true);
    setResult(null);
    setProgress({ done: 0, total });
    const agg = { created: 0, skipped_duplicates: 0, errors: [], skipped_details: [], created_leads: [], enriched: 0 };
    let jobId = null, poll = null;
    try {
      // Server-side progress record reflects REAL per-row dedup/enrichment work, not just upload bytes.
      try { const j = await api.post("/import-jobs/start", { filename, total }); jobId = j.data.job_id; } catch { /* fall back to per-batch */ }
      if (jobId) {
        poll = setInterval(async () => {
          try { const { data } = await api.get(`/import-jobs/${jobId}`); if (data) setProgress({ done: Math.min(data.processed, total), total }); } catch { /* ignore */ }
        }, 1200);
      }
      for (let start = 0; start < total; start += CHUNK) {
        const chunk = rows.slice(start, start + CHUNK);
        const { data } = await api.post("/leads/bulk-import", { rows: chunk, filename, assign_to: assignTo || null, job_id: jobId });
        if (data.pending_approval) {
          toast.info(data.message || "Import submitted for approval");
          if (poll) clearInterval(poll);
          setProgress(null);
          setBusy(false);
          return;
        }
        agg.created += data.created || 0;
        agg.skipped_duplicates += data.skipped_duplicates || 0;
        agg.enriched += data.enriched || 0;
        (data.errors || []).forEach((e) => agg.errors.push({ ...e, row: (e.row || 0) + start }));
        (data.skipped_details || []).forEach((s) => agg.skipped_details.push({ ...s, row: (s.row || 0) + start }));
        (data.created_leads || []).forEach((l) => agg.created_leads.push(l));
        if (!jobId) setProgress({ done: Math.min(start + CHUNK, total), total });
        setResult({ ...agg });
      }
      setResult({ ...agg });
      setProgress({ done: total, total });
      setAssigned({});
      loadHistory();
      toast.success(`${agg.created} imported · ${agg.skipped_duplicates} duplicates skipped${agg.enriched ? ` · ${agg.enriched} enriched` : ""}`);
    } catch (e) {
      setResult({ ...agg });
      toast.error(`${e.response?.data?.detail || "Import failed"} — ${agg.created} rows imported before the error`);
    } finally { if (poll) clearInterval(poll); setBusy(false); }
  };

  const sample = "First Name,Last Name,Company Name,Email,Title,Mobile Phone,Industry,# Employees,Country\nRamesh,Kumar,Tata Steel,ramesh@tatasteel.com,L&D Head,+91 98765 43210,Metals,10000+,India\nAhmed,Saleh,ADNOC,ahmed@adnoc.ae,HR Director,+971 50 123 4567,Oil & Gas,10000+,UAE";

  return (
    <>
      <PageHeader eyebrow="ENTERS FUNNEL AT SUSPECT · APOLLO ENRICHMENT WHEN CONFIGURED" title="Bulk CSV Import" hint="Upload a CSV of companies and contacts. Rows enter the funnel at Suspect, are de-duplicated against existing accounts, and enriched via Apollo when a key is configured." />
      <div className="p-8 max-w-4xl space-y-6">
        <div className="border border-[#DFDAD0] bg-white p-5">
          <label className="flex items-center gap-3 cursor-pointer">
            <Upload className="w-5 h-5 text-[#1C2530]/50" strokeWidth={1.5} />
            <input type="file" accept=".csv,text/csv" onChange={onFile} data-testid="csv-file-input" className="text-sm" />
          </label>
          <button onClick={() => { const p = parseCSV(sample); setParsed(p); setMapping(autoMap(p.headers)); }}
            data-testid="load-sample-btn" className="mt-3 font-mono text-[10px] uppercase tracking-wider text-[#3E8F99] hover:underline">
            or load sample data
          </button>
        </div>

        {parsed && (
          <div className="border border-[#DFDAD0] bg-white p-5">
            <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 mb-4">Column Mapping · {parsed.rows.length} rows detected</div>
            <div className="grid grid-cols-2 gap-3">
              {FIELDS.map((f) => (
                <div key={f.key} className="flex items-center gap-2">
                  <div className="w-40 text-xs text-[#1C2530]/80">{f.label}</div>
                  <select data-testid={`map-${f.key}`} value={mapping[f.key] ?? ""} onChange={(e) => setMapping((m) => ({ ...m, [f.key]: e.target.value === "" ? null : +e.target.value }))}
                    className="flex-1 border border-[#DFDAD0] px-2 py-1 text-xs bg-white outline-none focus:border-[#1C2530]">
                    <option value="">— none —</option>
                    {parsed.headers.map((h, i) => <option key={i} value={i}>{h}</option>)}
                  </select>
                </div>
              ))}
            </div>
            {(user.role === "admin" || user.role === "supervisor") && (
              <div className="mt-4 flex items-center gap-2" data-testid="assign-to-row">
                <div className="w-40 text-xs text-[#1C2530]/80">Assign imported leads to</div>
                <select data-testid="assign-to-select" value={assignTo} onChange={(e) => setAssignTo(e.target.value)}
                  className="flex-1 border border-[#DFDAD0] px-2 py-1.5 text-xs bg-white outline-none focus:border-[#1C2530]">
                  <option value="">— leave unassigned (route via Allocation Queue) —</option>
                  {reps.map((r) => <option key={r.id} value={r.id}>{r.name} · {r.role}{r.region ? ` · ${r.region}` : ""}</option>)}
                </select>
              </div>
            )}
            <button onClick={doImport} disabled={busy || !mappingValid} data-testid="do-import-btn"
              className="mt-5 flex items-center gap-2 bg-[#1C2530] text-[#F5F3EE] px-4 py-2 text-xs font-mono uppercase tracking-wider hover:bg-[#2a3644] disabled:opacity-40">
              {busy ? (progress && progress.total ? `Importing… ${Math.round((progress.done / progress.total) * 100)}%` : "Importing…") : "Import to Suspect"} <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {parsed && mappingValid && (
          <div className="border border-[#DFDAD0] bg-white p-5" data-testid="import-preview">
            <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 mb-4">
              Preview · first {previewRows.length} of {parsed.rows.length} mapped rows
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[#1C2530]/50 border-b border-[#DFDAD0]">
                    <th className="py-1.5 pr-3 font-mono uppercase tracking-wider text-[10px]">Contact</th>
                    <th className="py-1.5 pr-3 font-mono uppercase tracking-wider text-[10px]">Account</th>
                    <th className="py-1.5 pr-3 font-mono uppercase tracking-wider text-[10px]">Email</th>
                    <th className="py-1.5 pr-3 font-mono uppercase tracking-wider text-[10px]">Title</th>
                    <th className="py-1.5 pr-3 font-mono uppercase tracking-wider text-[10px]">Geo</th>
                  </tr>
                </thead>
                <tbody data-testid="preview-tbody">
                  {previewRows.map((r, i) => (
                    <tr key={i} className="border-b border-[#F0EDE6]">
                      <td className="py-1.5 pr-3">{r.contact_name || <span className="text-[#B23A2E]">— missing —</span>}</td>
                      <td className="py-1.5 pr-3">{r.account_name || <span className="text-[#B23A2E]">— missing —</span>}</td>
                      <td className="py-1.5 pr-3 text-[#1C2530]/70">{r.email || "—"}</td>
                      <td className="py-1.5 pr-3 text-[#1C2530]/70">{r.title || "—"}</td>
                      <td className="py-1.5 pr-3 text-[#1C2530]/70">{r.geography || "India"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {progress && (
          <div className="border border-[#DFDAD0] bg-white p-5" data-testid="import-progress">
            <div className="flex items-center justify-between mb-2 font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">
              <span data-testid="import-progress-label">{busy ? "Importing" : "Imported"} · {progress.done.toLocaleString()} / {progress.total.toLocaleString()} rows <span className="text-[#1C2530]/40">· sent in batches, no row limit</span></span>
              <span>{progress.total ? Math.round((progress.done / progress.total) * 100) : 0}%</span>
            </div>
            <div className="w-full h-2 bg-[#EFECE5] overflow-hidden">
              <div className="h-full bg-[#2F7A3E]" data-testid="import-progress-bar"
                style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%`, transition: "width 200ms ease" }} />
            </div>
          </div>
        )}

        {result && (
          <div className="border border-[#3E8F99] bg-[#3E8F99]/5 p-5" data-testid="import-result">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div><div className="font-mono text-2xl text-[#2F7A3E]">{result.created}</div><div className="font-mono text-[10px] uppercase text-[#1C2530]/60">Created</div></div>
              <div><div className="font-mono text-2xl text-[#E2711D]">{result.skipped_duplicates}</div><div className="font-mono text-[10px] uppercase text-[#1C2530]/60">Duplicates skipped</div></div>
              <div><div className="font-mono text-2xl text-[#B23A2E]">{result.errors.length}</div><div className="font-mono text-[10px] uppercase text-[#1C2530]/60">Errors</div></div>
            </div>
            {result.errors.length > 0 && (
              <div className="mt-3 font-mono text-[11px] text-[#B23A2E]">
                {result.errors.map((e, i) => <div key={i}>Row {e.row}: {e.error}</div>)}
              </div>
            )}
            {result.enriched > 0 && (
              <div className="mt-3 font-mono text-[10px] uppercase tracking-wider text-[#2F7A3E]" data-testid="enriched-note">
                {result.enriched} contact{result.enriched > 1 ? "s" : ""} Apollo-enriched (phone / LinkedIn)
              </div>
            )}

            {result.created_leads?.length > 0 && (
              <div className="mt-5" data-testid="suggested-reps">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">
                    Suggested rep per lead{result.created_leads.length > 200 ? ` · showing first 200 of ${result.created_leads.length.toLocaleString()}` : ""} · approve here or in Allocation Queue (never auto-assigned)
                  </div>
                  {result.created_leads.some((l) => l.suggested_rep && !assigned[l.lead_id]) && (
                    <button onClick={assignAll} data-testid="assign-all-btn"
                      className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-[#3E8F99] hover:underline">
                      <Check className="w-3.5 h-3.5" /> Assign all suggested
                    </button>
                  )}
                </div>
                {(() => {
                  const mp = result.created_leads.filter((l) => (l.missing || []).includes("phone")).length;
                  const ml = result.created_leads.filter((l) => (l.missing || []).includes("linkedin")).length;
                  const pend = result.created_leads.filter((l) => l.phone_pending).length;
                  return (mp || ml || pend) ? (
                    <div className="mb-2 font-mono text-[10px] text-[#E2711D]" data-testid="enrichment-gaps">
                      Still missing: {mp} phone · {ml} LinkedIn{pend ? ` · ${pend} phone reveal pending (Apollo webhook)` : ""}
                    </div>
                  ) : null;
                })()}
                <div className="overflow-x-auto max-h-64 overflow-y-auto border border-[#DFDAD0] bg-white">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-[#F5F3EE]">
                      <tr className="text-left text-[#1C2530]/50 border-b border-[#DFDAD0]">
                        <th className="py-1.5 px-3 font-mono uppercase tracking-wider text-[10px]">Contact</th>
                        <th className="py-1.5 px-3 font-mono uppercase tracking-wider text-[10px]">Account</th>
                        <th className="py-1.5 px-3 font-mono uppercase tracking-wider text-[10px]">Region</th>
                        <th className="py-1.5 px-3 font-mono uppercase tracking-wider text-[10px]">Missing</th>
                        <th className="py-1.5 px-3 font-mono uppercase tracking-wider text-[10px]">Suggested rep</th>
                        <th className="py-1.5 px-3 font-mono uppercase tracking-wider text-[10px]">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.created_leads.slice(0, 200).map((l, i) => (
                        <tr key={i} className="border-b border-[#F0EDE6]">
                          <td className="py-1.5 px-3">{l.contact_name}{l.enriched && <span className="ml-1.5 text-[#2F7A3E] text-[9px] font-mono uppercase">· enriched</span>}</td>
                          <td className="py-1.5 px-3 text-[#1C2530]/70">{l.account_name}</td>
                          <td className="py-1.5 px-3 text-[#1C2530]/70">{l.region}</td>
                          <td className="py-1.5 px-3">
                            {(l.missing || []).length === 0
                              ? <span className="text-[#2F7A3E] text-[10px] font-mono uppercase">complete</span>
                              : (l.missing || []).map((m) => (
                                  <span key={m} className="mr-1 inline-block px-1.5 py-0.5 bg-[#E2711D]/10 text-[#E2711D] text-[9px] font-mono uppercase">{m}</span>
                                ))}
                            {l.phone_pending && <span className="inline-block px-1.5 py-0.5 bg-[#3E8F99]/10 text-[#3E8F99] text-[9px] font-mono uppercase">phone pending</span>}
                          </td>
                          <td className="py-1.5 px-3">
                            {l.suggested_rep
                              ? <span className="text-[#2F7A3E]">{l.suggested_rep.name} <span className="text-[#1C2530]/40">· {l.suggested_rep.open_leads} open</span></span>
                              : l.assigned_to ? <span className="text-[#1C2530]/50">assigned to you</span>
                              : <span className="text-[#E2711D]">no covering rep — assign manually</span>}
                          </td>
                          <td className="py-1.5 px-3">
                            {assigned[l.lead_id]
                              ? <span className="flex items-center gap-1 text-[#2F7A3E] font-mono text-[10px] uppercase"><Check className="w-3 h-3" /> {assigned[l.lead_id]}</span>
                              : l.suggested_rep
                                ? <button data-testid={`assign-suggested-${i}`} onClick={() => assignRep(l)}
                                    className="font-mono text-[10px] uppercase tracking-wider text-[#3E8F99] hover:underline">Assign</button>
                                : <span className="text-[#1C2530]/30">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {result.skipped_details?.length > 0 && (
              <div className="mt-5" data-testid="dedupe-report">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">
                    Duplicates skipped · {result.skipped_details.length} rows
                  </div>
                  <button onClick={downloadDedupeReport} data-testid="download-dedupe-btn"
                    className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-[#3E8F99] hover:underline">
                    <Download className="w-3.5 h-3.5" /> Download report
                  </button>
                </div>
                <div className="overflow-x-auto max-h-48 overflow-y-auto border border-[#DFDAD0] bg-white">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-[#F5F3EE]">
                      <tr className="text-left text-[#1C2530]/50 border-b border-[#DFDAD0]">
                        <th className="py-1.5 px-3 font-mono uppercase tracking-wider text-[10px]">Row</th>
                        <th className="py-1.5 px-3 font-mono uppercase tracking-wider text-[10px]">Contact</th>
                        <th className="py-1.5 px-3 font-mono uppercase tracking-wider text-[10px]">Email</th>
                        <th className="py-1.5 px-3 font-mono uppercase tracking-wider text-[10px]">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.skipped_details.slice(0, 200).map((s, i) => (
                        <tr key={i} className="border-b border-[#F0EDE6]">
                          <td className="py-1.5 px-3 text-[#1C2530]/60">{s.row}</td>
                          <td className="py-1.5 px-3">{s.contact_name}</td>
                          <td className="py-1.5 px-3 text-[#1C2530]/70">{s.email || "—"}</td>
                          <td className="py-1.5 px-3 text-[#E2711D]">{s.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {history.length > 0 && (
          <div className="border border-[#DFDAD0] bg-white p-5" data-testid="import-history">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 mb-4">
              <History className="w-3.5 h-3.5" /> Recent Imports · audit log
            </div>
            <div className="overflow-x-auto max-h-64 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-[#F5F3EE]">
                  <tr className="text-left text-[#1C2530]/50 border-b border-[#DFDAD0]">
                    <th className="py-1.5 px-3 font-mono uppercase tracking-wider text-[10px]">When</th>
                    <th className="py-1.5 px-3 font-mono uppercase tracking-wider text-[10px]">File</th>
                    <th className="py-1.5 px-3 font-mono uppercase tracking-wider text-[10px]">By</th>
                    <th className="py-1.5 px-3 font-mono uppercase tracking-wider text-[10px]">Created</th>
                    <th className="py-1.5 px-3 font-mono uppercase tracking-wider text-[10px]">Dupes</th>
                    <th className="py-1.5 px-3 font-mono uppercase tracking-wider text-[10px]">Errors</th>
                    <th className="py-1.5 px-3 font-mono uppercase tracking-wider text-[10px]">Enriched</th>
                    <th className="py-1.5 px-3 font-mono uppercase tracking-wider text-[10px]">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h, i) => (
                    <tr key={i} className={`border-b border-[#F0EDE6] ${h.undone ? "opacity-40" : ""}`}>
                      <td className="py-1.5 px-3 text-[#1C2530]/60">{new Date(h.at).toLocaleString()}</td>
                      <td className="py-1.5 px-3">{h.filename}</td>
                      <td className="py-1.5 px-3 text-[#1C2530]/70">{h.by_name}</td>
                      <td className="py-1.5 px-3 text-[#2F7A3E]">{h.created}</td>
                      <td className="py-1.5 px-3 text-[#E2711D]">{h.skipped_duplicates}</td>
                      <td className="py-1.5 px-3 text-[#B23A2E]">{h.errors}</td>
                      <td className="py-1.5 px-3 text-[#3E8F99]">{h.enriched}</td>
                      <td className="py-1.5 px-3">
                        {h.undone
                          ? <span className="font-mono text-[9px] uppercase text-[#1C2530]/50">rolled back</span>
                          : h.created > 0
                            ? <button data-testid={`undo-import-${i}`} onClick={() => undoImport(h)}
                                className="font-mono text-[10px] uppercase tracking-wider text-[#B23A2E] hover:underline">Undo</button>
                            : <span className="text-[#1C2530]/30">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
